import "./GalleryDesktop.css";
import "./GalleryMobile.css";
import "./GalleryShared.css";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";

import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Modal from "./Modal";
import { defaultImages } from "./galleryImages";

const LEFT = 37;
const RIGHT = 39;
const offset = 1;

const GALLERY_WIDTH = 1920; // natural coordinate space
const DEFAULT_RATIO = 4 / 3;

// Returns [{images, height, widths}] for a justified layout.
// Each row's height = GALLERY_WIDTH / Σ(aspectRatios), so cell widths
// (= height × ratio) sum exactly to GALLERY_WIDTH and every cell has the
// image's natural aspect ratio — no distortion, no letterboxing.
function calcJustifiedLayout(rows, aspectRatios) {
  if (!rows.length) return [];

  return rows.map((row) => {
    if (!row.length) return { images: row, height: 0, widths: [] };
    const sumRatios = row.reduce(
      (s, img) => s + (aspectRatios[img.lowRes] ?? aspectRatios[img.src] ?? DEFAULT_RATIO),
      0
    );
    const height = GALLERY_WIDTH / sumRatios;
    return {
      images: row,
      height,
      widths: row.map((img) => {
        const r = aspectRatios[img.lowRes] ?? aspectRatios[img.src] ?? DEFAULT_RATIO;
        return height * r; // sums to GALLERY_WIDTH
      }),
    };
  });
}

const Gallery = ({ galleryImages, type, rowConfig = [], style = {} }) => {
  const images = (galleryImages || defaultImages).map((rest, index) => ({
    ...rest,
    id: index + 1,
  }));

  const [selected, setSelected] = useState(undefined);
  const [newImages, setNewImages] = useState(images);
  const [aspectRatios, setAspectRatios] = useState({});

  const selectedRef = useRef();

  const previousImage = useMemo(
    () => images[selected?.id - 1 - offset],
    [images, selected?.id]
  );
  const nextImage = useMemo(
    () => images[selected?.id + 1 - offset],
    [images, selected?.id]
  );

  const onBack = useCallback(() => {
    if (previousImage) setSelected(previousImage);
  }, [previousImage]);

  const onNext = useCallback(() => {
    if (nextImage) setSelected(nextImage);
  }, [nextImage]);

  // Keyboard navigation
  useEffect(() => {
    const handleLeft = (e) => { if (e.keyCode === LEFT) onBack(); };
    const handleRight = (e) => { if (e.keyCode === RIGHT) onNext(); };
    window.addEventListener("keydown", handleLeft);
    window.addEventListener("keydown", handleRight);
    return () => {
      window.removeEventListener("keydown", handleLeft);
      window.removeEventListener("keydown", handleRight);
    };
  }, [onNext, onBack, selected]);

  const handleClick = (image, e) => {
    selectedRef.current = e.target;
    setSelected(image);
  };

  // Load low-res images and capture aspect ratios
  useEffect(() => {
    const clone = [...images];
    images.forEach((image, index) => {
      const img = new Image();
      img.src = image.lowRes;
      img.onload = () => {
        clone[index].src = image.lowRes;
        setNewImages([...clone]);
        if (image.lowRes) {
          setAspectRatios((prev) => ({
            ...prev,
            [image.lowRes]: img.naturalWidth / img.naturalHeight,
          }));
        }
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mobile / flat layout (no rowConfig) ────────────────────────────────
  const setRow = (imgs) =>
    imgs.map((image) => (
      <div
        id={image.name}
        key={image.id}
        onClick={(e) => handleClick(image, e)}
        className={`container ${selected === image.name ? "active" : ""}`}
      >
        <div className={`img ${image.src !== image.thumbnail ? "loaded" : ""}`}>
          <img
            className={`img ${image.src !== image.thumbnail ? "loaded" : ""}`}
            alt={image.title}
            id={image.name}
            src={image.src}
          />
          <div id={image.name} className="overlay">
            <p id={image.name}>{image.title}</p>
          </div>
        </div>
      </div>
    ));

  // ── Desktop justified layout ────────────────────────────────────────────
  const getGalleryImagesPerRow = () => {
    let idx = 0;
    const rows = rowConfig.reduce((acc, count, i) => {
      for (let j = 0; j < count; j++, idx++) {
        if (idx < newImages.length) {
          acc[i] = [...(acc[i] || []), newImages[idx]];
        }
      }
      return acc;
    }, []);
    if (idx < newImages.length) rows.push(newImages.slice(idx));
    return rows;
  };

  const getGalleryRows = () => {
    const rawRows = getGalleryImagesPerRow();
    const layout = calcJustifiedLayout(rawRows, aspectRatios);

    if (!layout.length) return null;

    return layout.map((row, index) => (
      <div
        key={index}
        style={{ display: "flex", height: row.height + "px", overflow: "hidden" }}
      >
        {row.images.map((image, ci) => (
          <div
            key={image.id}
            id={image.name}
            onClick={(e) => handleClick(image, e)}
            className={`container ${selected === image.name ? "active" : ""}`}
            style={{ width: row.widths[ci] + "px", flexShrink: 0 }}
          >
            <div
              className={`img ${image.src !== image.thumbnail ? "loaded" : ""}`}
            >
              <img
                className={`img ${image.src !== image.thumbnail ? "loaded" : ""}`}
                alt={image.title}
                id={image.name}
                src={image.src}
              />
              <div id={image.name} className="overlay">
                <p id={image.name}>{image.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    ));
  };

  const galleryStyle = rowConfig.length
    ? { ...style, display: "block", width: GALLERY_WIDTH + "px" }
    : style;

  return (
    <>
      <Modal
        onBack={onBack}
        onNext={onNext}
        isFirst={!previousImage}
        isLast={!nextImage}
        selected={selected}
        deselect={() => setSelected(undefined)}
      />
      <div
        id="gallery"
        className={`gallery ${type ? "gallery-" + type : "gallery-main"}`}
        style={galleryStyle}
      >
        {rowConfig.length ? getGalleryRows() : setRow(newImages)}
      </div>
    </>
  );
};

export default Gallery;
